import React from 'react';
import { PortfolioKPIData } from '../../contexts/PortfolioKPIContext';

interface PortfolioBriefModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRefresh: () => void;
  isLoading: boolean;
  brief: string | null;
  error: string | null;
  cached: boolean;
  kpis: PortfolioKPIData;
}

const formatDollar = (value: number): string => {
  const abs = Math.abs(value);
  const formatted = abs >= 1000
    ? abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : abs.toFixed(2);
  return value >= 0 ? `+$${formatted}` : `-$${formatted}`;
};

const formatDollarNeutral = (value: number): string => {
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const pnlColor = (value: number): string => {
  if (value > 0) return '#22C55E';
  if (value < 0) return '#EF4444';
  return '#9CA3AF';
};

/**
 * Parse text containing markdown links [text](url) into React elements.
 */
const renderTextWithLinks = (text: string): React.ReactNode => {
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  while ((match = linkRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(
      <a
        key={match.index}
        href={match[2]}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: '#A78BFA', textDecoration: 'underline', textUnderlineOffset: '2px' }}
      >
        {match[1]}
      </a>
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? <>{parts}</> : text;
};

const PortfolioBriefModal: React.FC<PortfolioBriefModalProps> = ({
  isOpen,
  onClose,
  onRefresh,
  isLoading,
  brief,
  error,
  cached,
  kpis,
}) => {
  if (!isOpen) return null;

  // gold.portfolio_current (each the sum of the position rows); null = "—".
  const s = kpis.summary;
  const portfolioValue = s?.value ?? null;
  const dailyPnl = s?.today_pnl ?? null;
  const mtd = s?.mtd_pnl ?? null;
  const avgYield = s?.avg_yield ?? null;
  const dailyTheta = s?.theta_per_day ?? null;
  const rulesFired = s?.rules_fired ?? 0;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: '#1F2937',
          borderRadius: '12px',
          border: '1px solid #374151',
          maxWidth: '640px',
          width: '100%',
          maxHeight: '80vh',
          overflow: 'auto',
          padding: '0',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 24px',
            borderBottom: '1px solid #374151',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#A78BFA"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
              <path d="M20 3v4" />
              <path d="M22 5h-4" />
              <path d="M4 17v2" />
              <path d="M5 18H3" />
            </svg>
            <h2 style={{ margin: 0, color: '#E5E7EB', fontSize: '18px', fontWeight: 600 }}>
              Market & Portfolio Brief
            </h2>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={onRefresh}
              disabled={isLoading}
              title="Refresh analysis"
              style={{
                background: 'none',
                border: 'none',
                color: isLoading ? '#4B5563' : '#9CA3AF',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                padding: '4px',
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
                borderRadius: '4px',
                transition: 'color 0.15s',
              }}
              onMouseEnter={(e) => { if (!isLoading) (e.currentTarget as HTMLButtonElement).style.color = '#A78BFA'; }}
              onMouseLeave={(e) => { if (!isLoading) (e.currentTarget as HTMLButtonElement).style.color = '#9CA3AF'; }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  animation: isLoading ? 'spin 1s linear infinite' : 'none',
                }}
              >
                <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
                <path d="M21 3v5h-5" />
              </svg>
            </button>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#9CA3AF',
              cursor: 'pointer',
              fontSize: '20px',
              padding: '4px',
              lineHeight: 1,
            }}
          >
            &times;
          </button>
          </div>
        </div>

        {/* Stats Row */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '12px',
          padding: '16px 24px',
          borderBottom: '1px solid #374151',
        }}>
          {/* Today's P&L */}
          <div style={{ backgroundColor: '#111827', borderRadius: '8px', padding: '12px' }}>
            <div style={{ fontSize: '11px', color: '#9CA3AF', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Today's P&L
            </div>
            <div style={{ fontSize: '16px', fontWeight: 600, color: pnlColor(dailyPnl ?? 0) }}>
              {dailyPnl != null ? formatDollar(dailyPnl) : '—'}
            </div>
            <div style={{ fontSize: '11px', color: pnlColor(mtd ?? 0), marginTop: '2px' }}>
              MTD {mtd != null ? formatDollar(mtd) : '—'}
            </div>
          </div>

          {/* Portfolio Value */}
          <div style={{ backgroundColor: '#111827', borderRadius: '8px', padding: '12px' }}>
            <div style={{ fontSize: '11px', color: '#9CA3AF', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Portfolio
            </div>
            <div style={{ fontSize: '16px', fontWeight: 600, color: '#E5E7EB' }}>
              {portfolioValue != null ? formatDollarNeutral(portfolioValue) : '—'}
            </div>
            <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '2px' }}>
              {kpis.positionCount} pos
            </div>
          </div>

          {/* Avg Yield */}
          <div style={{ backgroundColor: '#111827', borderRadius: '8px', padding: '12px' }}>
            <div style={{ fontSize: '11px', color: '#9CA3AF', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Yield
            </div>
            <div style={{ fontSize: '16px', fontWeight: 600, color: '#E5E7EB' }}>
              {avgYield != null ? `${(avgYield * 100).toFixed(1)}%` : '—'}
            </div>
            <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '2px' }}>
              annualized
            </div>
          </div>

          {/* Daily Theta */}
          <div style={{ backgroundColor: '#111827', borderRadius: '8px', padding: '12px' }}>
            <div style={{ fontSize: '11px', color: '#9CA3AF', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Theta
            </div>
            <div style={{ fontSize: '16px', fontWeight: 600, color: '#E5E7EB' }}>
              {dailyTheta != null ? `$${dailyTheta.toFixed(0)}` : '—'}
            </div>
            <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '2px' }}>
              per day
            </div>
          </div>
        </div>

        {/* Alerts */}
        {rulesFired > 0 && (
          <div style={{
            padding: '10px 24px',
            borderBottom: '1px solid #374151',
          }}>
            <span style={{ fontSize: '13px', color: '#F59E0B' }}>
              {'\u26A0'} {rulesFired} position{rulesFired > 1 ? 's' : ''}: a rule fired
            </span>
          </div>
        )}

        {/* Body */}
        <div style={{ padding: '24px' }}>
          <style>{`
            @keyframes spin { to { transform: rotate(360deg); } }
          `}</style>

          {isLoading && !brief && (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div
                style={{
                  width: '36px',
                  height: '36px',
                  border: '3px solid #374151',
                  borderTopColor: '#7C3AED',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 16px',
                }}
              />
              <p style={{ color: '#9CA3AF', fontSize: '14px', margin: 0 }}>
                Connecting to analyst...
              </p>
            </div>
          )}

          {error && !isLoading && (
            <div
              style={{
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                padding: '16px',
                color: '#FCA5A5',
                fontSize: '14px',
              }}
            >
              {error}
            </div>
          )}

          {brief && (
            <div style={{ color: '#D1D5DB', fontSize: '14px', lineHeight: 1.7 }}>
              {(() => {
                // Parse sections: split on ## headings
                const sections: { title?: string; body: string; isBottomLine?: boolean }[] = [];
                const parts = brief.split(/^## /m);

                // First part before any heading (if any)
                const preamble = parts[0].trim();
                if (preamble) {
                  sections.push({ body: preamble });
                }

                // Remaining parts each start with "Title\nBody"
                for (let i = 1; i < parts.length; i++) {
                  const newlineIdx = parts[i].indexOf('\n');
                  if (newlineIdx === -1) {
                    sections.push({ title: parts[i].trim(), body: '' });
                  } else {
                    const title = parts[i].substring(0, newlineIdx).trim();
                    const body = parts[i].substring(newlineIdx + 1).trim();
                    const isBottomLine = title.toLowerCase().includes('bottom line');
                    sections.push({ title, body, isBottomLine });
                  }
                }

                return sections.map((section, idx) => {
                  // Bottom Line gets special styling
                  if (section.isBottomLine) {
                    return (
                      <div
                        key={idx}
                        style={{
                          marginTop: '20px',
                          padding: '14px 16px',
                          backgroundColor: 'rgba(167, 139, 250, 0.08)',
                          border: '1px solid rgba(167, 139, 250, 0.2)',
                          borderRadius: '8px',
                        }}
                      >
                        <div style={{
                          fontSize: '11px',
                          fontWeight: 700,
                          color: '#A78BFA',
                          textTransform: 'uppercase',
                          letterSpacing: '1px',
                          marginBottom: '6px',
                        }}>
                          {section.title}
                        </div>
                        <div style={{ color: '#E5E7EB', fontSize: '14px', lineHeight: 1.6 }}>
                          {renderTextWithLinks(section.body)}
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={idx} style={{ marginBottom: '18px' }}>
                      {section.title && (
                        <h3 style={{
                          margin: '0 0 6px',
                          fontSize: '14px',
                          fontWeight: 600,
                          color: '#E5E7EB',
                        }}>
                          {section.title}
                        </h3>
                      )}
                      {section.body.split('\n\n').map((para, pIdx) => (
                        <p key={pIdx} style={{ margin: '0 0 8px', color: '#9CA3AF', fontSize: '13px', lineHeight: 1.7 }}>
                          {renderTextWithLinks(para)}
                        </p>
                      ))}
                    </div>
                  );
                });
              })()}
              {isLoading && (
                <span style={{
                  display: 'inline-block',
                  width: '2px',
                  height: '14px',
                  backgroundColor: '#A78BFA',
                  marginLeft: '2px',
                  animation: 'blink 1s step-end infinite',
                  verticalAlign: 'text-bottom',
                }} />
              )}
              <style>{`
                @keyframes blink { 50% { opacity: 0; } }
              `}</style>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PortfolioBriefModal;
